/**
 * CR109: Shared Font Registry
 * 
 * Single source of truth for all available fonts.
 * Used by Theme Labs (global tokens) and Layout Studio (page UI fonts).
 */

export type FontCategory = "sans" | "serif";

export type FontRecommendation = "ui" | "headlines" | "both";

export interface FontRegistryItem {
  /** Unique identifier stored in config */
  id: string;
  /** Display label in UI */
  label: string;
  /** Font category */
  category: FontCategory;
  /** Full CSS font-family with fallbacks */
  cssFamily: string;
  /** Recommendation hint */
  recommendedFor: FontRecommendation;
}

/**
 * All available fonts with their CSS font-family values.
 * IDs are stored in config; cssFamily is resolved at render time.
 */
export const FONT_REGISTRY: FontRegistryItem[] = [
  {
    id: "inter",
    label: "Inter",
    category: "sans",
    cssFamily:
      '"Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif',
    recommendedFor: "both",
  },
  {
    id: "manrope",
    label: "Manrope",
    category: "sans",
    cssFamily:
      '"Manrope", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif',
    recommendedFor: "both",
  },
  {
    id: "dm_sans",
    label: "DM Sans",
    category: "sans",
    cssFamily:
      '"DM Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif',
    recommendedFor: "both",
  },
  {
    id: "space_grotesk",
    label: "Space Grotesk",
    category: "sans",
    cssFamily:
      '"Space Grotesk", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif',
    recommendedFor: "headlines",
  },
  {
    id: "libre_caslon_text",
    label: "Libre Caslon Text",
    category: "serif",
    cssFamily:
      '"Libre Caslon Text", ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
    recommendedFor: "headlines",
  },
  {
    id: "source_sans_3",
    label: "Source Sans 3",
    category: "sans",
    cssFamily:
      '"Source Sans 3", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif',
    recommendedFor: "both",
  },
  {
    id: "montserrat",
    label: "Montserrat",
    category: "sans",
    cssFamily:
      '"Montserrat", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, "Noto Sans", "Liberation Sans", sans-serif',
    recommendedFor: "headlines",
  },
];

/** Default font ID for body text */
export const DEFAULT_BODY_FONT_ID = "inter";

/** Default font ID for headings */
export const DEFAULT_HEADING_FONT_ID = "inter";

/**
 * Get a font item by its ID.
 * Returns undefined if not found.
 */
export function getFontById(id?: string | null): FontRegistryItem | undefined {
  if (!id) return undefined;
  return FONT_REGISTRY.find((f) => f.id === id);
}

/**
 * Resolve a font ID to its CSS font-family string.
 * Falls back to the specified fallbackId (or Inter) if ID is unknown.
 */
export function resolveFontFamily(
  id?: string | null,
  fallbackId: string = DEFAULT_BODY_FONT_ID
): string {
  const font = getFontById(id);
  if (font) return font.cssFamily;
  
  const fallback = getFontById(fallbackId);
  if (fallback) return fallback.cssFamily;
  
  // Ultimate fallback
  return FONT_REGISTRY[0]!.cssFamily;
}

/**
 * Get recommendation badge text for UI display.
 */
export function getRecommendationLabel(recommendedFor: FontRecommendation): string {
  switch (recommendedFor) {
    case "ui":
      return "UI";
    case "headlines":
      return "Headlines";
    case "both":
      return "UI + Headlines";
  }
}
